import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { formatDate } from 'date-fns';
import type { ReactNode } from 'react';
import { useState } from 'react';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Drawer } from '@sorare/core/src/atoms/layout/Drawer';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import { ANY_SPORT_SCORES_MATCHES_DAY_GAMEID } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';

import { PlayerScoreDetailsView } from '#src/components/MatchView/PlayerScoreDetailsView/index.tsx';
import { Game } from '#src/components/game/Game/index.tsx';
import { GameCardsPreview } from '#src/components/scoutingCenter/gameCenter/GameCardsPreview/index.tsx';

import type { GameSection_gameWithCardsPreview } from './__generated__/index.graphql.ts';

type Props = {
  title?: ReactNode;
  selectedGameId: string | undefined;
  games: GameSection_gameWithCardsPreview[];
  cardsRarities: Rarity[];
  loading: boolean;
};

export const GamesSection = ({
  title,
  selectedGameId,
  games,
  cardsRarities,
  loading,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const [selectedPlayerParams, setSelectedPlayerParams] = useState<
    | {
        playerSlug: string;
        gameId: string;
      }
    | undefined
  >();

  return (
    <>
      {!!selectedPlayerParams && (
        <Drawer open={!!selectedPlayerParams} side="right">
          <PlayerScoreDetailsView
            playerSlug={selectedPlayerParams.playerSlug}
            gameId={selectedPlayerParams.gameId}
            onClose={() => setSelectedPlayerParams(undefined)}
            side="right"
          />
        </Drawer>
      )}
      <Vertical gap={1.5}>
        {title}
        {games.map(gameWithCardsPreview => (
          <Skeleton loading={loading} key={gameWithCardsPreview.game.id}>
            <Game
              game={gameWithCardsPreview.game}
              selected={
                idFromObject(gameWithCardsPreview.game.id) ===
                idFromObject(selectedGameId)
              }
              to={generateSportContextPath(
                ANY_SPORT_SCORES_MATCHES_DAY_GAMEID,
                {
                  params: {
                    day: formatDate(
                      gameWithCardsPreview.game.date,
                      'yyyy-MM-dd'
                    ),
                    gameId: idFromObject(gameWithCardsPreview.game.id),
                  },
                }
              )}
            >
              {(gameWithCardsPreview.cardsWithPlayerGameScores || []).length >
                0 && (
                <GameCardsPreview
                  gameWithCardsPreview={gameWithCardsPreview}
                  cardsRarities={cardsRarities}
                  onCardClick={playerSlug =>
                    setSelectedPlayerParams({
                      playerSlug,
                      gameId: gameWithCardsPreview.game.id,
                    })
                  }
                />
              )}
            </Game>
          </Skeleton>
        ))}
      </Vertical>
    </>
  );
};

GamesSection.fragments = {
  gameWithCardsPreview: gql`
    fragment GameSection_gameWithCardsPreview on GameWithCardsPreview {
      id
      game {
        id
        date
        ...Game_anyGameInterface
      }
      ...GameCardsPreview_gameWithCardsPreview
    }
    ${GameCardsPreview.fragments.gameWithCardsPreview}
    ${Game.fragments.anyGameInterface}
  ` as TypedDocumentNode<GameSection_gameWithCardsPreview>,
};
